import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, Trash2, Users, Phone, Save, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import EmptyState from '@/components/EmptyState';
import { useDemo } from '@/contexts/DemoContext';

/**
 * EmergencyContactsPage - Emergency contacts for binder
 */

const CONTACT_TYPES = [
  { value: 'emergency', label: 'Emergency Contact' },
  { value: 'family', label: 'Family' },
  { value: 'caregiver', label: 'Caregiver' },
  { value: 'babysitter', label: 'Babysitter' },
  { value: 'neighbor', label: 'Neighbor' },
  { value: 'workplace', label: 'Workplace Contact' },
];

const EmergencyContactsPage = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const { isDemoMode, getProfiles, getEmergencyContacts } = useDemo();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    fetchProfile();
  }, [profileId, isDemoMode]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      if (isDemoMode) {
        const profiles = getProfiles() || [];
        const demoProfile = profiles.find(p => p.id === profileId) || profiles[0];
        const demoContacts = getEmergencyContacts() || [];
        // Filter contacts for this profile
        const profileContacts = demoContacts.filter(c => 
          c.profile_id === profileId || !c.profile_id
        );
        setProfile(demoProfile);
        setContacts(profileContacts);
        setLoading(false);
        return;
      }
      
      const response = await API.get(`/emergency-binder/profiles/${profileId}`);
      setProfile(response.data);
      setContacts(response.data.emergency_contacts || []);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      // Don't show toast for demo mode or missing profiles
      if (!isDemoMode) {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (isDemoMode) {
        toast.success('Emergency contacts saved (demo)');
        setSaving(false);
        return;
      }
      
      await API.put(`/emergency-binder/profiles/${profileId}`, {
        emergency_contacts: contacts
      });
      toast.success('Emergency contacts saved');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save contacts');
    } finally {
      setSaving(false);
    }
  };

  const addContact = () => {
    setContacts([...contacts, {
      id: `temp_${Date.now()}`,
      name: '',
      relationship: '',
      phone: '',
      secondary_phone: '',
      address: '',
      contact_type: 'emergency'
    }]);
  };

  const updateContact = (index, field, value) => {
    setContacts(contacts.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  const removeContact = (index) => {
    setContacts(contacts.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="emergency-contacts-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/emergency-binder`)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-800">Emergency Contacts</h1>
              <p className="text-xs text-slate-500">{profile?.name}</p>
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-500" />
                Contacts
              </CardTitle>
              <Button variant="outline" size="sm" onClick={addContact} data-testid="add-contact-btn">
                <Plus className="h-4 w-4 mr-1" />
                Add Contact
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {contacts.length === 0 ? (
              <EmptyState 
                type="emergencyContacts"
                onAction={addContact}
                actionLabel="Add First Contact"
              />
            ) : (
              contacts.map((contact, index) => (
                <div key={contact.id || index} className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 space-y-3">
                      {/* Full name - full width */}
                      <Input
                        placeholder="Full name"
                        value={contact.name || ''}
                        onChange={(e) => updateContact(index, 'name', e.target.value)}
                      />
                      {/* Contact type - full width */}
                      <Select
                        value={contact.contact_type || 'emergency'}
                        onValueChange={(value) => updateContact(index, 'contact_type', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Contact type" />
                        </SelectTrigger>
                        <SelectContent>
                          {CONTACT_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {/* Relationship - full width */}
                      <Input
                        placeholder="Relationship"
                        value={contact.relationship || ''}
                        onChange={(e) => updateContact(index, 'relationship', e.target.value)}
                      />
                      {/* Primary phone - full width */}
                      <Input
                        placeholder="Primary phone"
                        value={contact.phone || ''}
                        onChange={(e) => updateContact(index, 'phone', e.target.value)}
                      />
                      {/* Secondary phone - full width */}
                      <Input
                        placeholder="Secondary phone (optional)"
                        value={contact.secondary_phone || ''}
                        onChange={(e) => updateContact(index, 'secondary_phone', e.target.value)}
                      />
                      {/* Address - full width */}
                      <Input
                        placeholder="Address (optional)"
                        value={contact.address || ''}
                        onChange={(e) => updateContact(index, 'address', e.target.value)}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 flex-shrink-0"
                      onClick={() => removeContact(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Button onClick={handleSave} disabled={saving} className="w-full h-12 bg-purple-600 hover:bg-purple-700">
          {saving ? (
            <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>
          ) : (
            <><CheckCircle2 className="h-4 w-4 mr-2" />Save Emergency Contacts</>
          )}
        </Button>
      </main>
    </div>
  );
};

export default EmergencyContactsPage;
